import { useCallback, useEffect, useMemo, useState } from "react";
import type { SortingState } from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { DataTableWrapper } from "@trillion/ui/components/admin/data-table-wrapper";
import { ConfirmDialog } from "@trillion/ui/components/admin/confirm-dialog";
import { SortableTableHead } from "@trillion/ui/components/admin/sortable-header";
import { SORTABLE_GRID_OPTIONS, orderByKey } from "@trillion/ui/lib/data-grid-sort";
import { Button } from "@trillion/ui/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@trillion/ui/components/ui/table";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { settingsApi } from "@/lib/admin-api";
import { getSettingsColumns } from "@/components/settings/settings-columns";
import { SettingsDialog } from "@/components/settings/settings-dialog";
import { ConvertToSecretDialog } from "@/components/settings/convert-to-secret-dialog";
import { jobSettingsScope } from "@/lib/job-details";
import type { SettingView } from "@/lib/settings-types";

/** One scope, so the key alone orders the grid. */
export const JOB_SETTINGS_DEFAULT_SORT: SortingState = [{ id: "key", desc: false }];

/**
 * One recurring job's own settings (sc-114201): the `job:<id>` scope's grid, reusing the Settings page's
 * columns and dialog, with "Add setting" preset to that scope. Loads once per mount and after each save or
 * delete; configuration, not job state, so it never polls.
 */
export function JobSettingsCard({ recurringJobId }: { recurringJobId: string }) {
  const scope = jobSettingsScope(recurringJobId);
  const [data, setData] = useState<SettingView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SettingView | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<SettingView | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [convertTarget, setConvertTarget] = useState<SettingView | null>(null);
  const [sorting, setSorting] = useState<SortingState>(JOB_SETTINGS_DEFAULT_SORT);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      setData(await settingsApi.list(scope));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load settings");
    } finally {
      setIsLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = useCallback(() => {
    setEditing(undefined);
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((s: SettingView) => {
    setEditing(s);
    setDialogOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await settingsApi.remove(deleteTarget.scope, deleteTarget.key);
      toast.success(`Deleted "${deleteTarget.key}"`);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete setting");
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, load]);

  const columns = useMemo(
    () =>
      getSettingsColumns({
        onEdit: openEdit,
        onDelete: setDeleteTarget,
        onConvertToSecret: setConvertTarget,
      }),
    [openEdit]
  );

  const rows = useMemo(() => orderByKey(data, (s) => s.key), [data]);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...SORTABLE_GRID_OPTIONS,
    getRowId: (s) => s.key,
    state: { sorting },
    onSortingChange: setSorting,
  });

  return (
    <>
      <DataTableWrapper
        title="Settings"
        description={`Settings scoped to ${scope}`}
        isLoading={isLoading}
        toolbar={
          <Button size="sm" onClick={openCreate}>
            <Plus className="size-4" />
            Add setting
          </Button>
        }
      >
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <SortableTableHead key={header.id} header={header} />
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  No settings for this job.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </DataTableWrapper>

      <SettingsDialog
        editing={editing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={load}
        presetScope={scope}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete setting"
        description={`Delete ${deleteTarget?.scope ?? ""} / ${deleteTarget?.key ?? ""}? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        variant="destructive"
        isLoading={isDeleting}
      />
      <ConvertToSecretDialog
        target={convertTarget}
        onClose={() => setConvertTarget(null)}
        onConverted={load}
      />
    </>
  );
}
